import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import StyledComponent from 'styled-components';

import ApiRequest from '../../api/request';
import { CookieValues, ModelNameEnum } from '../../types';
import { currency } from '../../utils';
import { AlertContext } from '../Alert';
import {
  FormInputChange,
  FormInputType,
  FormInputTypeEnum,
  FormInputValues,
} from '../Form/Form.types';
import FormValidated from '../Form/FormValidated';
import TransitionModal from '../TransitionModal';
import EnhancedTableFilter from './EnhancedTableFilter';
import EnhancedTableHead from './EnhancedTableHead';
import EnhancedTableToolbar from './EnhancedTableToolbar';
import { EnhancedTableProps, EnhancedTableRef } from './Table.types';
import TableUtils from './Table.utils';

const checkboxSelectedBg = '#F50057';
const Container = StyledComponent.div`
  .table-inner
  {
    width: '100%';
    margin-bottom: 0;
  }
  .table-wrapper
  {
    overflow-x: auto;
    width: 100%;
    display: block;
  }
  .table
  {
    min-width: 750;
  }
  .MuiCheckbox-colorSecondary.Mui-checked
  {
    color: ${checkboxSelectedBg} !important;
  }

  width: '100%';
  margin-top: 0;
  padding: 24;
`;

const EnhancedTable = forwardRef<EnhancedTableRef, EnhancedTableProps>(
  (
    {
      customButton1,
      isCreateAvailable,
      isUpdateAvailable,
      isCsvAvailable,
      isDeleteAvailable,
      sortEnabled,
      onCreateFormInputsChanged,
      onUpdateFormInputsChanged,
      model,
      tableDidFinishLoading,
      tableDidStartLoading,
      multiSelectEnabled,
      selectAllEnabled,
      headers,
      updateFormInputs,
      createFormInputs,
      data,
      defaultSort,
    }: EnhancedTableProps,
    tableRef,
  ) => {
    /**
     * This is a hacky way to access the form inputs in a useCallback without needing to re-evaluate it
     * @todo make this not require refs
     */
    const createFormInputsRef = useRef<FormInputType[]>([]);

    const [cookies] = useCookies<'token', CookieValues>(['token']);

    const { showAlert } = useContext(AlertContext);

    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<string>(defaultSort);
    const [selected, setSelected] = useState<any[]>([]);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(25);
    const [isUpdateModalVisible, setIsUpdateModalVisible] = useState<boolean>(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
    const [filters, setFilters] = useState<{ [key: string]: string }>({});

    const tableUtils = useMemo(() => TableUtils.GetInstance(), []);

    // MARK: - Sorting
    const handleRequestSort = (event: any, property: string) => {
      const isDesc = orderBy === property && order === 'desc';
      setOrder(isDesc ? 'asc' : 'desc');
      setOrderBy(property);
    };

    const handleSelectAllClick = (event: any) => {
      if (event.target.checked) {
        const newSelecteds = data.map(row => row._id);
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    };

    const tableRowOnClick = useCallback((_: any, id: string) => {
      setSelected(prevSelected => {
        const selectedIndex = prevSelected.indexOf(id);
        let newSelected: any[] = [];

        if (multiSelectEnabled) {
          if (selectedIndex === -1) {
            newSelected = newSelected.concat(prevSelected, id);
          } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(prevSelected.slice(1));
          } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(prevSelected.slice(0, -1));
          } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
              prevSelected.slice(0, selectedIndex),
              prevSelected.slice(selectedIndex + 1),
            );
          }
        } else {
          newSelected = selectedIndex === -1 ? [id] : [];
        }
        return newSelected;
      });
    }, [multiSelectEnabled, selected.length]);

    const handleChangePage = (_: any, newPage: number) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const updateRecordForField = (iFieldName: string, iRow: any) => {
      let fieldName = iFieldName;
      let fieldNameInPtr = '';
      let row = iRow;

      // Get total pointers in key
      const occurrences = (fieldName.match(/\./g) || []).length;
      if (occurrences === 0) {
        const updateFormInput = updateFormInputs.find(input => {
          if ([input.id, input.key].includes(iFieldName)) {
            return true;
          }
          return false;
        });

        row[fieldName] = updateFormInput?.value ?? '';
        return row;
      }

      // Iterate all pointers
      let splitIndex = -1;
      for (let i = 0; i < occurrences; i++) {
        splitIndex = fieldName.indexOf('.');
        try {
          // Extract pointer
          fieldNameInPtr = fieldName.substring(splitIndex + 1);
          fieldName = fieldName.substring(0, splitIndex);

          // Slowly parse down the data
          if (fieldNameInPtr.indexOf('.') !== -1) {
            row = row[fieldName];
          } else {
            const updateFormInput = updateFormInputs.find(input => {
              if (
                [input.id, input.key].includes(iFieldName.substr(iFieldName.lastIndexOf('.') + 1))
              ) {
                return true;
              }
              return false;
            });

            row[fieldName][fieldNameInPtr] = updateFormInput?.value;
            return row;
          }
          fieldName = fieldNameInPtr;
        } catch (err) {
          console.log(`Pointer: ${fieldNameInPtr} not found in property ${fieldName}`);
          return row;
        }
      }
    };

    /**
     * state.selected only contains unique ID's for selected rows and not the actual indices.
     * This is a helper function to convert selected ID's to actual table data at the rows of the unique ID's.
     *
     * @param param Parameters
     *  isForCsv  If data is for csv
     */
    const getTableDataForSelectedRows = (params: any) => {
      try {
        const allFormattedData = [];
        let selectedRecord = null;
        let headerId = '';
        let ptrData: string | number = '';

        // Iterate all selected IDs
        for (let i = 0; i < selected.length; i++) {
          let formattedData: any = {};

          // Find record for this ID
          selectedRecord = data.find(record => record._id === selected[i]);

          // If formatting for CSV export we need to pad data
          if (params && params.isForCsv) {
            // Iterate table headers
            for (let j = 0; j < headers.length; j++) {
              headerId = headers[j].id;
              ptrData = tableUtils.extractValueFromPointer(headerId, selectedRecord);
              if (typeof ptrData === 'object' && ptrData !== null) {
                // Build formatted string
                let formattedString = '';
                // @ts-ignore
                for (let k = 0; k < ptrData.length; k++) {
                  const ptrDataKeys = Object.keys(ptrData[k]);
                  for (let l = 0; l < ptrDataKeys.length; l++) {
                    formattedString += ptrData[k][ptrDataKeys[l]];
                    if (l !== ptrDataKeys.length - 1) {
                      formattedString += ':';
                    }
                  }
                  // @ts-ignore
                  if (k !== ptrData.length - 1) {
                    formattedString += ',';
                  }
                }
                // ptrData = JSON.stringify(ptrData, undefined, 2);
                ptrData = formattedString;
              }
              if (ptrData !== null && ptrData !== undefined) {
                try {
                  formattedData[headers[j].label] = ptrData.toString().replace(/<br\/>/gi, ' | ');
                } catch(err: any) {
                  console.error(err);
                  console.log('ptrData', ptrData);
                }
              } else {
                formattedData[headers[j].label] = '';
              }
            }
          } // Otherwise just send JSON object
          else {
            formattedData = selectedRecord;
          }
          // Add to list
          allFormattedData.push(formattedData);
        }

        return allFormattedData;
      } catch (err: any) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: `An error has occurred, please try again or contact support.\nError: ${err.emssage}`,
          variant: 'danger',
          dismissText: 'okay',
          err,
        });
        return [];
      }
    };

    /**
    When button action finishes we notify delegate and then delegate invokes this method
    passing in table data and this will return the data

    Didn't want to make the tableData part of the state here
    and didn't want to make the parent responsible for button logic
  */
    const handleAction = useCallback(
      (action: any, tableData: any) => {
        switch (action.type) {
          case 'create': {
            let inserted = false;
            // Add to table
            let modifiedData = [...tableData];

            // No data
            if (modifiedData === null) {
              modifiedData = [action.data];
              inserted = true;
            } else if (modifiedData.length === 0) {
              modifiedData.push(action.data);
              inserted = true;
            }

            // Find position
            let lhs = -1;
            let found = false;
            let i = 0;
            const cmp = tableUtils.getComparator(order, orderBy);

            while (!found && i < modifiedData.length && !inserted) {
              lhs = cmp(modifiedData[i], action.data);

              // New first element
              if (lhs <= 0) {
                modifiedData.splice(i, 0, action.data);
                found = true;
              } else {
                i++;
              }
            }
            if (!found && !inserted) {
              modifiedData.push(action.data);
            }
            return modifiedData;
          }

          case 'delete': {
            // Remove from table
            const modifiedData = [...tableData];
            const length = Array.isArray(selected) ? selected.length : 1;
            let selectedRowIdx = -1;
            for (let i = 0; i < length; i++) {
              selectedRowIdx = modifiedData
                .map(data => data._id)
                .indexOf(Array.isArray(selected) ? selected[i] : selected);
              if (selectedRowIdx !== -1) {
                modifiedData.splice(selectedRowIdx, 1);
              } else {
                console.log(`Table.handleAction(${action.type}) Couldn't find index to remove`);
              }
            }

            setSelected([]);

            return modifiedData;
          }
          default:
            return tableData;
        }
      },
      [selected, order, orderBy, tableUtils],
    );

    // MARK: - Edit button related
    // Close modal
    const updateModalOnClose = () => {
      setIsUpdateModalVisible(false);
    };

    // Show modal
    const updateButtonOnClick = () => {
      const selectedRecord = data.find(record => record._id === selected[0]);
      const newUpdateFormInputs = [...updateFormInputs];
      let key = '';

      const lastElement = newUpdateFormInputs[newUpdateFormInputs.length - 1];
      const missingSubmitButton =
        lastElement.type !== 'submit' || lastElement.value !== 'Update Record';

      const iterationLength = missingSubmitButton
        ? newUpdateFormInputs.length
        : newUpdateFormInputs.length - 1;
      // Iterate inputs (skipping submit button)
      for (let i = 0; i < iterationLength; i++) {
        // Allow caller to overwrite the key if needed
        // Useful for SELECT element where ID is text we want to display
        // and key is where the value is located
        key = newUpdateFormInputs[i].key ?? '';
        if (!key.length) {
          key = newUpdateFormInputs[i].id;
        }

        // If reference field we want to give them the pointer value and let the FormValiateds
        // option list handle displaying the proper text based off keyInReference
        // Make sure this is a reference field first too
        if (newUpdateFormInputs[i].type === 'select' && newUpdateFormInputs[i].reference) {
          key = `${newUpdateFormInputs[i].id}._id`;
        }
        const value = tableUtils.extractValueFromPointer(key, selectedRecord);
        newUpdateFormInputs[i].value = value;

        if (
          model === ModelNameEnum.configuration &&
          ['true', 'false'].includes(newUpdateFormInputs[i].value)
        ) {
          newUpdateFormInputs[i].type = FormInputTypeEnum.boolean;
          newUpdateFormInputs[i].element = 'select';
          newUpdateFormInputs[i].options = [
            { text: 'True', value: 'true' },
            { text: 'False', value: 'false' },
          ];
        }
      }

      const updateSubmitButton = getUpdateSubmitButton();
      if (missingSubmitButton) {
        newUpdateFormInputs.push(updateSubmitButton);
      } else {
        newUpdateFormInputs[newUpdateFormInputs.length - 1] = updateSubmitButton;
      }

      setIsUpdateModalVisible(true);
      onUpdateFormInputsChanged(newUpdateFormInputs);
    };

    // The actual form
    const updateForm = () => {
      if (!isUpdateModalVisible) {
        return null;
      }
      return (
        <div className="left-aligned">
          <FormValidated
            formInputs={updateFormInputs}
            formOnChange={updateFormOnChange}
            validateOnInit
            columns={1}
            dynamicInputs={false}
            description=""
          />
        </div>
      );
    };

    // Handle modify form on change
    const updateFormOnChange = (change: FormInputChange, _: boolean) => {
      // console.log("OnChange " + change.id + " set to " + change.value + ' type ' + change.type);

      if (change.type !== 'submit') {
        const key = change.id.substr(change.id.lastIndexOf('.') + 1);

        const newFormInputs = [...updateFormInputs];
        const updateFormInput = newFormInputs.find(input => {
          if ([input.id, input.key].includes(key)) {
            return true;
          }
          return false;
        });

        if (updateFormInput) {
          updateFormInput.value = change.value;
        }
        onUpdateFormInputsChanged(newFormInputs);
      }
    };

    // Send updated form to backend
    const updateFormOnSubmit = useCallback(
      async (evt: any, _: any, updateFormInputs: FormInputValues[]) => {
        evt.preventDefault();

        tableDidStartLoading();

        // console.log(this.state);
        // Build form data
        const formData = new FormData();
        let fieldId = null;
        for (let i = 0; i < updateFormInputs.length; i++) {
          // Don't process submit button
          if (updateFormInputs[i].type !== 'submit') {
            fieldId = updateFormInputs[i].id.substr(updateFormInputs[i].id.lastIndexOf('.') + 1);
            if (updateFormInputs[i].type === 'file') {
              formData.append(fieldId, updateFormInputs[i].value);
            } else if (updateFormInputs[i].type !== 'submit') { 
              // Skip buttons
                formData.set(fieldId, updateFormInputs[i].value);
            }
          }
        }

        // Send over ID of selected row
        formData.set('id', selected[0]);
        formData.set('model', model);

        try {
          const response = await ApiRequest.sendRequest({
            method: 'post',
            params: formData,
            path: 'data/update',
            token: cookies.token,
            contentType: 'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW',
          });
          // Success
          if (response.data.error === null) {
            const recordFromDbWithNewValues = response.data.results;

            // Update table data with modified record
            const modifiedData = [...data];
            const modifiedIndex = modifiedData
              .map(record => record._id)
              .indexOf(recordFromDbWithNewValues._id);
            let updatedRecord = modifiedData[modifiedIndex];

            // Iterate update form and update our table with new data
            for (let i = 0; i < updateFormInputs.length - 1; i++) {
              if (updateFormInputs[i].managedUpdateForm) {
                const { id } = updateFormInputs[i];
                updatedRecord = updateRecordForField(id, recordFromDbWithNewValues);
              }
            }
            modifiedData[modifiedIndex] = updatedRecord;

            // Notify parent to save data in state
            tableDidFinishLoading(modifiedData, response.data.message);

            setIsUpdateModalVisible(false);
          }
          // Failed
          else {
            showAlert({
              headerText: 'Uh-oh',
              detailText: response.data.error,
              variant: 'danger',
              dismissText: 'okay',
            });
          }
        } catch (err: any) {
          showAlert({
            headerText: 'Uh-oh',
            detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
            variant: 'danger',
            dismissText: 'okay',
          });
        }
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [tableDidFinishLoading, model, selected],
    );

    // MARK: - Create button related
    // Close modal
    const createModalOnClose = () => {
      setIsCreateModalVisible(false);
    };

    // Show modal
    const createButtonOnClick = () => {
      setIsCreateModalVisible(true);
    };

    // Handle modify form on change
    const createFormOnChange = useCallback(
      (change: FormInputChange, _: boolean) => {
        const key = change.id.substr(change.id.lastIndexOf('.') + 1);
        const newFormInputs = [...createFormInputs];
        const createFormInput = newFormInputs.find(input => {
          if ([input.id, input.key].includes(key)) {
            return true;
          }
          return false;
        });

        if (createFormInput) {
          createFormInput.value = change.value;
        }
        onCreateFormInputsChanged(newFormInputs);
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [createFormInputs],
    );

    // The actual form
    const createForm = useCallback(() => {
      if (!isCreateModalVisible) {
        return null;
      }

      return (
        <div className="left-aligned">
          <FormValidated
            formInputs={createFormInputs}
            formOnChange={createFormOnChange}
            validateOnInit
            columns={1}
            dynamicInputs={false}
            description=""
          />
        </div>
      );
    }, [isCreateModalVisible, createFormInputs, createFormOnChange]);

    // Send updated form to backend
    const createFormOnSubmit = useCallback(
      async (evt: any) => {
        evt.preventDefault();

        tableDidStartLoading();

        // Build form data
        const formData = new FormData();
        let fieldId = null;
        const createFormInputs = createFormInputsRef.current;
        for (let i = 0; i < createFormInputs.length; i++) {
          fieldId = createFormInputs[i].id.substr(createFormInputs[i].id.lastIndexOf('.') + 1);
          if (createFormInputs[i].type === 'file') {
            formData.append(fieldId, createFormInputs[i].value);
          } else if (createFormInputs[i].type !== 'submit') { // Skip buttons
            // Array
            if (createFormInputs[i].multiSelectEnabled) {
              const value =
                createFormInputs[i].value === undefined
                  ? '_empty_array_'
                  : createFormInputs[i].value;
              formData.set(fieldId, value);
            } // Single value
            else {
              formData.set(fieldId, createFormInputs[i].value);
            }
          }
        }
        // Send over ID of selected row
        formData.set('model', model);
        try {
          const action = {
            type: 'create',
            data: null,
          };

          const response = await ApiRequest.sendRequest({
            method: 'post',
            params: formData,
            path: 'data/create',
            token: cookies.token,
            contentType: 'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW',
          });
          // Success
          if (response.data.error === null) {
            action.data = response.data.results;

            // Notify parent
            tableDidFinishLoading(action, response.data.message);

            setIsCreateModalVisible(false);
          }
          // Failed
          else {
            showAlert({
              headerText: 'Uh-oh',
              detailText: response.data.error?.message ?? response.data.error,
              variant: 'danger',
              dismissText: 'okay',
            });
          }
        } catch (err: any) {
          showAlert({
            headerText: 'Uh-oh',
            detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
            variant: 'danger',
            dismissText: 'okay',
          });
        }
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [tableDidFinishLoading, cookies.token, showAlert, model],
    );

    // MARK: - Filter related
    const filterOnChange = (id: string, evt: any) => {
      setFilters(prevFilters => {
        const newFilters = { ...prevFilters };
        if (evt.target.value.length > 0) {
          newFilters[id] = evt.target.value.toLowerCase();
        } else {
          delete newFilters[id];
        }

        return newFilters;
      });
    };

    const getUpdateSubmitButton = useCallback(() => {
      setPage(0);
      setFilters({});

      return {
        label: '',
        id: 'submit',
        element: 'input',
        type: 'submit',
        value: 'Update Record',
        onClick: updateFormOnSubmit,
        class: 'btn-block  .border .border--white',
        disabled: true,
        placeholder: '',
        row: 1,
        col: 1,
        required: '',
      } as FormInputType;
    }, [updateFormOnSubmit]);

    const getCreateSubmitButton = useCallback(() => {
      setSelected([]);
      setPage(0);
      setFilters({});

      return {
        label: '',
        id: 'submit',
        element: 'input',
        type: 'submit',
        value: 'Create Record',
        onClick: createFormOnSubmit,
        class: 'btn-block  .border .border--white',
        disabled: true,
        placeholder: '',
        row: 1,
        col: 1,
        required: '',
      } as FormInputType;
    }, [createFormOnSubmit]);

    const filteredData = useMemo(() => {
      if (!data) {
        return [];
      }
      return tableUtils.applyFiltersAndSort({
        data,
        order,
        orderBy,
        filters,
        headers,
      });
    }, [data, order, orderBy, filters, headers, tableUtils]);

    const renderTableBody = useCallback(
      () =>
        filteredData
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((row, rowIndex) => {
            const isItemSelected = selected.indexOf(row._id) !== -1;
            const labelId = `enhanced-table-checkbox-${rowIndex}`;
            let tableCells = null;
            if (row) {
              // Iterate header columns and output rows of data
              tableCells = headers.map((header, headerIndex) => {
                // console.log(row);
                const visibleTextFieldName = header.id; // Header ID is the field in our data we want to display
                let visibleText: any = '';

                // Photo
                if (
                  header.id.indexOf('photo') !== -1 ||
                  header.id.indexOf('member.imageURL') !== -1
                ) {
                  try {
                    const src = `${tableUtils.extractValueFromPointer(visibleTextFieldName, row)}`;
                    visibleText = src ? (
                      <div className="image-cropper">
                        <img src={src} alt="Profile" className="profile-pic" />
                      </div>
                    ) : (
                      ''
                    );
                  } catch(err: any) {
                    console.error(err);
                    console.log('row', row);
                  }
                } else {
                  visibleText = tableUtils.extractValueFromPointer(visibleTextFieldName, row);
                }
                // First column
                if (headerIndex === 0) {
                  let temp =
                    visibleTextFieldName === 'time' ||
                    visibleTextFieldName === 'createdOn' ||
                    visibleTextFieldName === 'pClass.time'
                      ? tableUtils.formatDate(visibleText)
                      : visibleText
                        ? visibleText.toString()
                        : '';
                  if (
                    ['totalRaised', 'raised', 'goal', 'amount', 'min', 'max'].includes(
                      visibleTextFieldName,
                    )
                  ) {
                    temp = currency.formatCurrency(temp);
                  }
                  return (
                    <TableCell
                      component="th"
                      key={`${rowIndex}-${headerIndex}`}
                      id={labelId}
                      scope="row"
                      padding="none">
                      {temp}
                    </TableCell>
                  );
                }

                // Time field
                if (
                  visibleTextFieldName === 'time' ||
                  visibleTextFieldName === 'createdOn' ||
                  visibleTextFieldName === 'pClass.time'
                ) {
                  return (
                    <TableCell key={`${rowIndex}-${headerIndex}`} align="left">
                      {tableUtils.formatDate(visibleText.toString())}
                    </TableCell>
                  );
                }
                if (header.isObject) {
                  return (
                    <TableCell key={`${rowIndex}-${headerIndex}`} align="left">
                      <pre>{visibleText !== undefined ? JSON.stringify(visibleText, null, 4) : ''}</pre>
                    </TableCell>
                  );
                }
                if (
                  ['totalRaised', 'raised', 'goal', 'amount', 'min', 'max'].includes(
                    visibleTextFieldName,
                  )
                ) {
                  visibleText = currency.formatCurrency(visibleText);
                }

                let style;
                if (visibleTextFieldName === 'hexValue') {
                  style = { backgroundColor: visibleText };
                }

                return (
                  <TableCell style={style} key={`${rowIndex}-${headerIndex}`} align="left">
                    {visibleText ?? ''}
                  </TableCell>
                );
              });
            }

            return (
              <TableRow
                hover
                onClick={event => tableRowOnClick(event, row._id)}
                role="checkbox"
                aria-checked={isItemSelected}
                tabIndex={-1}
                key={row._id}
                selected={isItemSelected}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isItemSelected}
                    inputProps={{ 'aria-labelledby': labelId }}
                    className="table-checkbox"
                  />
                </TableCell>
                {tableCells}
              </TableRow>
            );
          }),
      [filteredData, page, rowsPerPage, selected, headers, tableUtils, tableRowOnClick],
    );

    useEffect(() => {
      createFormInputsRef.current = createFormInputs;
    }, [createFormInputs]);

    useImperativeHandle(tableRef, () => ({
      getCreateSubmitButton,
      handleAction,
    }));

    const emptyRows =
      rowsPerPage - Math.min(rowsPerPage, (data ? data.length : 0) - page * rowsPerPage);
    return (
      <Container>
        <Paper className="table-inner">
          <EnhancedTableFilter
            filters={headers ? headers.filter(header => header.filter) : []}
            filterValues={filters}
            onChange={filterOnChange}
            disabled={selected.length !== 0}
          />
          <EnhancedTableToolbar
            isCsvAvailable={isCsvAvailable}
            isDeleteAvailable={isDeleteAvailable}
            isUpdateAvailable={isUpdateAvailable}
            updateButtonOnClick={updateButtonOnClick}
            isCreateAvailable={isCreateAvailable}
            createButtonOnClick={createButtonOnClick}
            headers={headers}
            customButton1={customButton1 ?? null}
            token={cookies.token}
            tableDidStartLoading={tableDidStartLoading}
            tableDidFinishLoading={tableDidFinishLoading}
            selected={selected}
            model={model}
            getTableDataForSelectedRows={getTableDataForSelectedRows}
          />
          <div className="table-wrapper">
            <Table
              className="table"
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table">
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={data ? data.length : 0}
                headers={headers}
                selectAllEnabled={selectAllEnabled}
                sortEnabled={sortEnabled !== undefined ? sortEnabled : true}
              />
              <TableBody>
                {renderTableBody()}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 25, 50]}
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <TransitionModal
          isOpen={isUpdateModalVisible}
          onClose={updateModalOnClose}
          modalContent={updateForm}
          title=""
          description=""
        />
        <TransitionModal
          isOpen={isCreateModalVisible}
          onClose={createModalOnClose}
          modalContent={createForm}
        />
      </Container>
    );
  },
);

export default EnhancedTable;
